import type { LucideIcon } from 'lucide-react';
import {
  Award,
  BadgeCheck,
  BatteryCharging,
  BriefcaseBusiness,
  Building2,
  CircleDollarSign,
  Factory,
  Globe2,
  Handshake,
  HeartHandshake,
  House,
  Leaf,
  PanelTop,
  Rows4,
  ShieldCheck,
  Sparkles,
  SunMedium,
  TreePine,
  Users,
  Wrench,
  Zap
} from 'lucide-react';

export type NavItem = {
  label: string;
  href: string;
};

export type IconTextItem = {
  icon: LucideIcon;
  title: string;
  description: string;
};

export type MetricItem = {
  value: string;
  label: string;
  description: string;
};

export type ServiceItem = {
  icon: LucideIcon;
  title: string;
  description: string;
  points: string[];
};

export type TestimonialItem = {
  name: string;
  role: string;
  company: string;
  quote: string;
  rating: number;
};

export type FaqItem = {
  question: string;
  answer: string;
};

export type ProjectItem = {
  title: string;
  location: string;
  capacity: string;
  description: string;
  result: string;
  highlight: string;
};

export type CaseStudyItem = {
  title: string;
  description: string;
  result: string;
};

export type ProductItem = {
  icon: LucideIcon;
  title: string;
  description: string;
  useCase: string;
};

export type JobOpening = {
  title: string;
  type: string;
  location: string;
  description: string;
};

export type CultureItem = {
  icon: LucideIcon;
  title: string;
  description: string;
};

export type SocialLink = {
  label: string;
  href: string;
  icon: LucideIcon;
};

export type ContactInfo = {
  address: string;
  phone: string;
  email: string;
  hours: string;
};

export const siteNavigation: NavItem[] = [
  { label: 'Home', href: '/' },
  { label: 'About Us', href: '/about' },
  { label: 'Services', href: '/services' },
  { label: 'Projects', href: '/projects' },
  { label: 'Products', href: '/products' },
  { label: 'Why Solar', href: '/why-solar' },
  { label: 'Contact', href: '/contact' }
];

export const companyHighlights: IconTextItem[] = [
  {
    icon: ShieldCheck,
    title: 'Trusted Delivery',
    description:
      'End-to-end project execution with compliance, safety, and transparent reporting.'
  },
  {
    icon: Handshake,
    title: 'Customer First',
    description:
      'Consultative design, transparent pricing, and post-installation support built around long-term value.'
  },
  {
    icon: Sparkles,
    title: 'Premium Quality',
    description:
      'High-efficiency components, neat installation standards, and a modern approach to energy infrastructure.'
  }
];

export const companyStats: MetricItem[] = [
  {
    value: '150+',
    label: 'Projects Delivered',
    description: 'Across residential, commercial, and industrial segments.'
  },
  {
    value: '35 MW+',
    label: 'Solar Capacity Installed',
    description: 'Reliable clean-energy systems commissioned at scale.'
  },
  {
    value: '98%',
    label: 'Client Satisfaction',
    description: 'Measured through repeat business and positive referrals.'
  },
  {
    value: '24/7',
    label: 'Monitoring Support',
    description: 'Remote performance visibility and responsive service coverage.'
  }
];

export const solarBenefits: IconTextItem[] = [
  {
    icon: Leaf,
    title: 'Lower carbon footprint',
    description:
      'Reduce emissions with clean electricity generated directly from your rooftop or facility.'
  },
  {
    icon: SunMedium,
    title: 'Stable long-term savings',
    description:
      'Offset rising electricity tariffs with predictable solar energy production.'
  },
  {
    icon: BatteryCharging,
    title: 'Energy independence',
    description:
      'Pair solar with storage to keep critical loads running during outages.'
  },
  {
    icon: BadgeCheck,
    title: 'Government support',
    description:
      'Take advantage of subsidies, tax benefits, and policy incentives where available.'
  }
];

export const featuredServices: ServiceItem[] = [
  {
    icon: House,
    title: 'Residential Solar Installation',
    description:
      'Elegant rooftop systems for villas, apartments, and housing societies.',
    points: ['Rooftop surveys', 'Net-metering support', 'Battery-ready design']
  },
  {
    icon: Building2,
    title: 'Commercial Solar Solutions',
    description:
      'Scaled systems that help offices, malls, and institutions cut operating costs.',
    points: ['Load analysis', 'Energy audits', 'ROI planning']
  },
  {
    icon: Factory,
    title: 'Industrial Solar Projects',
    description:
      'Heavy-duty systems engineered for plants, warehouses, and manufacturing sites.',
    points: ['High-load design', 'Safety compliance', 'O&M contracts']
  },
  {
    icon: Wrench,
    title: 'Solar Maintenance',
    description:
      'Preventive maintenance programs and performance audits for long-term efficiency.',
    points: ['Module cleaning', 'Electrical checks', 'Performance reports']
  },
  {
    icon: Users,
    title: 'Solar Consultation',
    description:
      'Clear feasibility studies, ROI projections, and subsidy guidance before you invest.',
    points: ['Site assessment', 'Proposal engineering', 'Financial guidance']
  },
  {
    icon: Rows4,
    title: 'Rooftop Solar Systems',
    description:
      'Compact, efficient rooftop packages engineered for space and budget constraints.',
    points: ['Structural review', 'Inverter planning', 'Aesthetic layout']
  }
];

export const testimonials: TestimonialItem[] = [
  {
    name: 'Rajesh Mehta',
    role: 'Operations Director',
    company: 'Mehta Textiles',
    quote:
      'PS Group Solar Energy delivered our industrial rooftop project ahead of schedule with excellent coordination and measurable savings from day one.',
    rating: 5
  },
  {
    name: 'Ananya Shah',
    role: 'Homeowner',
    company: 'Ahmedabad',
    quote:
      'The team explained everything clearly, handled approvals smoothly, and the installation looks premium. Our monthly bill dropped immediately.',
    rating: 5
  },
  {
    name: 'Vikram Patel',
    role: 'Facility Manager',
    company: 'Patel Logistics',
    quote:
      'Reliable engineering, responsive service, and a practical plan for expansion made the entire solar rollout easy to trust.',
    rating: 5
  }
];

export const faqItems: FaqItem[] = [
  {
    question: 'How long does a typical solar installation take?',
    answer:
      'Most rooftop residential projects are completed within a few days after approvals, while larger commercial and industrial deployments are planned in phases.'
  },
  {
    question: 'Can solar work during power cuts?',
    answer:
      'Yes, when paired with batteries or hybrid inverters, solar can keep selected loads active during outages.'
  },
  {
    question: 'Do you help with subsidies and approvals?',
    answer:
      'Yes. We support the complete process from feasibility and design to documentation, approvals, and commissioning.'
  },
  {
    question: 'What maintenance is required?',
    answer:
      'Solar systems need periodic cleaning, performance checks, and inspection of cables, inverters, and mounting structures.'
  }
];

export const projectGallery: ProjectItem[] = [
  {
    title: 'Premium Villas Solar Roof',
    location: 'Pune, Maharashtra',
    capacity: '18 kW',
    description:
      'A clean rooftop installation with high-efficiency panels and backup-ready inverter design.',
    result: '78% reduction in monthly electricity cost',
    highlight: 'Residential'
  },
  {
    title: 'Retail Mall Solar Canopy',
    location: 'Surat, Gujarat',
    capacity: '250 kW',
    description:
      'A visible brand-building installation that offsets common-area loads and parking canopies.',
    result: 'Annual savings of over ?28 lakhs',
    highlight: 'Commercial'
  },
  {
    title: 'Warehouse Rooftop Power Plant',
    location: 'Indore, Madhya Pradesh',
    capacity: '1.2 MWp',
    description:
      'A large industrial rooftop system designed for peak operational reliability and remote monitoring.',
    result: '3.4 year payback target',
    highlight: 'Industrial'
  },
  {
    title: 'School Solar Retrofit',
    location: 'Jaipur, Rajasthan',
    capacity: '42 kW',
    description:
      'A teaching-friendly project showing how institutions can turn roofs into long-term savings.',
    result: 'Cut grid draw by 64%',
    highlight: 'Institutional'
  }
];

export const caseStudies: CaseStudyItem[] = [
  {
    title: 'Commercial rooftop modernization',
    description:
      'We reconfigured the rooftop layout, improved shading analysis, and optimized inverter placement for higher generation.',
    result: 'Reduced grid draw by 41%'
  },
  {
    title: 'Industrial peak-shaving strategy',
    description:
      'A hybrid architecture helped the client reduce peak tariffs while improving backup reliability for critical lines.',
    result: 'Shorter payback, better uptime'
  },
  {
    title: 'Community solar program',
    description:
      'A phased rollout for shared rooftops brought savings to an entire residential community without compromising aesthetics.',
    result: 'Lower common area expenses'
  }
];

export const products: ProductItem[] = [
  {
    icon: PanelTop,
    title: 'Solar Panels',
    description:
      'High-efficiency modules engineered for strong generation and premium aesthetics.',
    useCase: 'Rooftops, ground mounts, and hybrid systems'
  },
  {
    icon: Globe2,
    title: 'Solar Inverters',
    description:
      'Smart inverters for reliable conversion, monitoring, and grid interaction.',
    useCase: 'Residential, commercial, and industrial systems'
  },
  {
    icon: BatteryCharging,
    title: 'Solar Batteries',
    description:
      'Storage systems that improve backup performance and energy flexibility.',
    useCase: 'Backup-ready and off-grid environments'
  },
  {
    icon: SunMedium,
    title: 'Solar Water Heaters',
    description:
      'Cost-saving thermal systems for homes, hospitals, hotels, and institutions.',
    useCase: 'Hot water demand reduction'
  },
  {
    icon: Sparkles,
    title: 'Solar Street Lights',
    description:
      'Stand-alone lighting solutions for roads, campuses, parking lots, and public spaces.',
    useCase: 'Outdoor lighting and municipal projects'
  }
];

export const whySolarBenefits: IconTextItem[] = [
  {
    icon: TreePine,
    title: 'Environmental value',
    description:
      'Reduce emissions, support cleaner air, and make your energy use more sustainable.'
  },
  {
    icon: CircleDollarSign,
    title: 'Operational savings',
    description:
      'Protect your business and household budgets from rising electricity tariffs.'
  },
  {
    icon: BadgeCheck,
    title: 'Subsidy potential',
    description:
      'Where eligible, subsidies and policy support can improve the economics of the project.'
  }
];

export const jobOpenings: JobOpening[] = [
  {
    title: 'Solar Design Engineer',
    type: 'Full-time',
    location: 'Ahmedabad / Hybrid',
    description:
      'Own design calculations, BOM preparation, and layout plans for rooftop and C&I projects.'
  },
  {
    title: 'Project Coordinator',
    type: 'Full-time',
    location: 'Gujarat',
    description:
      'Coordinate site execution, vendors, documentation, and commissioning milestones.'
  },
  {
    title: 'Sales Consultant',
    type: 'Full-time',
    location: 'Multiple Locations',
    description:
      'Guide residential and commercial customers through discovery, proposals, and closure.'
  }
];

export const cultureItems: CultureItem[] = [
  {
    icon: Award,
    title: 'High standards',
    description: 'We value precision, accountability, and a premium customer experience.'
  },
  {
    icon: BriefcaseBusiness,
    title: 'Practical growth',
    description: 'We support career development through learning, responsibility, and trust.'
  },
  {
    icon: HeartHandshake,
    title: 'Team mindset',
    description: 'We work closely across design, execution, and service to deliver outcomes.'
  }
];

export const founderAchievements = [
  'Built a customer-focused solar delivery model for homes, businesses, and industries.',
  'Led high-performance teams across sales, engineering, and operations.',
  'Championed clean-energy adoption through practical and cost-effective project planning.',
  'Established a reputation for transparent communication and quality execution.'
];

export const socialLinks: SocialLink[] = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com', icon: Globe2 },
  { label: 'Instagram', href: 'https://www.instagram.com', icon: SunMedium },
  { label: 'Facebook', href: 'https://www.facebook.com', icon: Sparkles },
  { label: 'YouTube', href: 'https://www.youtube.com', icon: Zap }
];

export const contactDetails: ContactInfo = {
  address: 'Vaibhav Nagar, Navipur Road, Hathras, Uttar Pradesh - 204101',
  phone: '+91 8433 135253',
  email: 'psgroupsolar@gmail.com',
  hours: 'Mon - Sat: 9:30 AM - 6:30 PM'
};

export const founderProfile = {
  name: 'Avanish Sagar',
  designation: 'Director',
  phone: '8077225235',
  image: '/Avanishsagar.jpeg',
  biography:
    'A visionary leader committed to helping households and enterprises transition to clean, affordable energy with dependable execution and customer-first thinking.',
  vision:
    'To build one of the most trusted renewable energy brands by delivering solar systems that combine technical excellence, service quality, and measurable impact.',
  leadershipMessage:
    'We believe solar adoption should feel practical, premium, and transparent from the first consultation to the final commissioning handover.'
};

export const cmdProfile = {
  name: 'Birpal Singh',
  designation: 'CMD',
  phone: '8433135253',
  image: '/birpal.jpeg',
  summary:
    'Leads strategic growth, customer relationships, and operational discipline across the company.'
};
